import { useState } from 'react'

import {
  useGetAdVisualPerformanceByIdQuery,
  AdVisualPerformanceItem,
} from 'src/Redux/adsCampaignApi'
import { numberToMoney, numberFormatCommas } from 'src/Constants/functions'

import { getCTR } from '../helpers'

export interface AdVisualRow {
  id: string
  name: string
  cover: string
  spent: string
  impressions: string
  clicks: string
  ctr: string
  uniqueClicks: string
  conversions: string
  costPerConversion: string
}

const toRow = (item: AdVisualPerformanceItem): AdVisualRow => ({
  id: String(item.adVisualId),
  name: item.name,
  cover: item.thumbnailUrl,
  spent: `$${numberToMoney(item.totalActualSpend)}`,
  impressions: String(numberFormatCommas(item.totalImpressions)),
  clicks: String(numberFormatCommas(item.totalClicks)),
  ctr: getCTR(item.totalClicks, item.totalImpressions),
  uniqueClicks: String(numberFormatCommas(item.totalUniqueClicks)),
  conversions: String(numberFormatCommas(item.totalConversions ?? 0)),
  costPerConversion:
    (item.totalConversions ?? 0) > 0
      ? `$${numberToMoney(item.totalActualSpend / (item.totalConversions ?? 0))}`
      : '$0.00',
})

interface UseAdVisualPerformanceResult {
  rows: AdVisualRow[]
  isLoading: boolean
  isError: boolean
  selectedVideo: { id: string; name: string } | null
  openVideo: (id: string, name: string) => void
  closeVideo: () => void
}

export const useAdVisualPerformance = (
  campaignId: string,
): UseAdVisualPerformanceResult => {
  const [selectedVideo, setSelectedVideo] = useState<{
    id: string
    name: string
  } | null>(null)

  const { data, isLoading, isError } = useGetAdVisualPerformanceByIdQuery(
    campaignId,
    { skip: !campaignId },
  )

  const source = data ?? []
  const rows = source.map(toRow)

  const openVideo = (id: string, name: string): void => {
    setSelectedVideo({ id, name })
  }

  const closeVideo = (): void => {
    setSelectedVideo(null)
  }

  return {
    rows,
    selectedVideo,
    openVideo,
    closeVideo,
    isLoading,
    isError,
  }
}
